import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { apiGet, apiPost } from '@/services/apiClient';
import type { AppUser, Role } from '@/types/api.types';

export function useUsersQuery() {
  return useQuery({
    queryKey: ['registrations', 'users'],
    queryFn: () => apiGet<AppUser[]>('Users/GetUsers'),
  });
}

export function useRolesQuery() {
  return useQuery({
    queryKey: ['registrations', 'roles'],
    queryFn: () => apiGet<Role[]>('Users/GetRoles'),
  });
}

export function useCurrentUserQuery() {
  const { accounts } = useMsal();
  const email = accounts[0]?.username ?? '';
  return useQuery({
    queryKey: ['registrations', 'current-user', email],
    queryFn: () => apiGet<AppUser | null>(`Users/GetUserByEmail?Email=${encodeURIComponent(email)}`),
    enabled: !!email,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAddUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (user: AppUser) => apiPost<AppUser, string>('Users/AddUser', user),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['registrations', 'users'] });
    },
  });
}

export function useEditUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (user: AppUser) => apiPost<AppUser, string>('Users/EditUser', user),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['registrations', 'users'] });
    },
  });
}
